import { useEffect, useState } from 'react'
import { AdyenCheckout, Dropin, Card } from '@adyen/adyen-web'
import '@adyen/adyen-web/styles/adyen.css'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AdyenEcomData } from '@/api/store-orders-types'

interface AdyenCheckoutProps {
	title: string
	isOpen: boolean
	onClose: () => void
	adyenData: AdyenEcomData
	onPaymentResult: (result: { resultCode: string; [key: string]: any }) => void
	onError: (error: any) => void
}

export function AdyenCheckoutComponent({
	title,
	isOpen,
	onClose,
	adyenData,
	onPaymentResult,
	onError
}: AdyenCheckoutProps) {
	const [checkoutContainerRef, setCheckoutContainerRef] = useState<HTMLDivElement | null>(null)
	const [dropinComponent, setDropinComponent] = useState<any>(null)

	useEffect(() => {
		if (!isOpen || !adyenData || !checkoutContainerRef) {
			return
		}

		const initializeCheckout = async () => {
			try {
				const checkout = await AdyenCheckout({
					clientKey: adyenData.clientKey,
					environment: adyenData.environment.toLowerCase() as 'test' | 'live',
					countryCode: adyenData.countryCode,
					session: {
						id: adyenData.sessionId,
						sessionData: adyenData.sessionData
					},
					onPaymentCompleted: (result: any) => {
						onPaymentResult(result)
					},
					onPaymentFailed: (result: any) => {
						onError(new Error(`Payment failed: ${result.resultCode}`))
					},
					onError: (error: any) => {
						onError(error)
					}
				})

				const dropin = new Dropin(checkout, {
					paymentMethodComponents: [Card]
				})
				dropin.mount(checkoutContainerRef!)
				setDropinComponent(dropin)
			} catch (error) {
				console.error('Failed to initialize Adyen checkout:', error)
				onError(error)
			}
		}

		initializeCheckout()

		// Cleanup on unmount or when dialog closes
		return () => {
			if (dropinComponent) {
				dropinComponent.unmount()
				setDropinComponent(null)
			}
		}
	}, [isOpen, adyenData, checkoutContainerRef])

	const handleClose = () => {
		if (dropinComponent) {
			dropinComponent.unmount()
			setDropinComponent(null)
		}
		onClose()
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className='max-w-md'>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>
				<div ref={el => setCheckoutContainerRef(el)} className='adyen-checkout-container min-h-[400px]' />
			</DialogContent>
		</Dialog>
	)
}
